import { AuthJWTPayload } from "../types/system/AuthJWT";
import ServerError from "../types/system/ServerError";
import Utilities from "../foundation/Utility";
import Configuration from "./Configuration";
import Logger from "./Logger";
import { MenuItem } from "../types/system/MenuItem";
import DisplayTypeSet from "../types/system/DisplayTypeSet";
import { CustomConfigSection } from "../state/system/Menu";
import { DisplayType } from "../types/system/DisplayType";

const ignoreAppStatePersist = [
    "refreshPromise",
    "menuItems",
    "generalItems",
    "configItems",
    "customSections",
    "displayConfig",
];

class ApplicationState {
    private static _instance: ApplicationState;

    name: string = "timetabling-solutions";
    mode: string = "development";

    menuItems: MenuItem[] = [];
    generalItems: MenuItem[] = [];
    configItems: MenuItem[] = [];
    customSections: CustomConfigSection[] = [];
    displayConfig: DisplayTypeSet = new DisplayTypeSet();

    private token: string = null;
    private rawJWT: string = null;
    private jwt: AuthJWTPayload = null;

    storeState() {
        sessionStorage.setItem(
            `${Configuration.appName}-state`,
            JSON.stringify(
                Object.entries(this).reduce((obj, [key, value]) => {
                    if (ignoreAppStatePersist.includes(key)) return obj;
                    obj[key] = value;
                    return obj;
                }, {}),
            ),
        );
    }
    clearState() {
        sessionStorage.removeItem(`${Configuration.appName}-state`);
        this.token = null;
        this.rawJWT = null;
        this.jwt = null;
    }

    addMenuItems(items: MenuItem[]) {
        this.menuItems = items;
    }
    addGeneralItems(items: MenuItem[]) {
        for (const item of items) {
            this.generalItems = this.generalItems.filter(
                x => x.name !== item.name,
            );
            this.generalItems.push(item);
        }
    }
    addConfigItems(items: MenuItem[]) {
        for (const item of items) {
            this.configItems = this.configItems.filter(
                x => x.name !== item.name,
            );
            this.configItems.push(item);
        }
    }
    addCustomSections(items: CustomConfigSection[]) {
        for (const section of items) {
            this.customSections = this.customSections.filter(
                x => x.name !== section.name,
            );
            this.customSections.push(section);
        }
    }

    addDisplayTypes(types: DisplayType[]) {
        this.displayConfig.clearTypes();
        for (const type of types) {
            this.displayConfig.addType(type);
        }
    }
    addDisplayType(type: DisplayType) {
        this.displayConfig.addType(type);
    }
    removeDisplayType(type: DisplayType | string) {
        this.displayConfig.removeType(type);
    }

    static get jwt() {
        return this.instance.jwt;
    }
    static get encryptionKey() {
        return this.instance.getSessionEncryptionKey();
    }
    static get instance(): ApplicationState {
        if (!ApplicationState._instance) {
            var sessionData = sessionStorage.getItem(
                `${Configuration.appName}-state`,
            );

            ApplicationState._instance = new ApplicationState();

            if (sessionData)
                ApplicationState._instance = Utilities.deserializeInto(
                    sessionData,
                    ApplicationState._instance,
                );

            ApplicationState._instance.refreshPromise = null;
        }

        return ApplicationState._instance;
    }
    static set instance(value: ApplicationState) {
        throw Utilities.createError("Cannot set app state");
    }
    static reset() {
        this.instance.clearState();
    }
    static async logout() {
        this.reset();
        await fetch("api/deauthorize", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${this.instance.rawJWT}`,
            },
        });
        document.location = this.instance.getLogoutPath();
    }

    async updateToken(token: string) {
        this.token = token;
        await this.getSessionJWT();
    }

    private refreshPromise: Promise<void> = null;
    async getSessionJWT() {
        let serverToken: string | null = null;
        const params = new URLSearchParams(window.location.search);
        if (params.get("refreshToken"))
            serverToken = params.get("refreshToken");
        params.delete("refreshToken");
        const newParams = params.toString();
        window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}${newParams ? `?${newParams}` : ""}${
                window.location.hash
            }`,
        );

        if (
            (!this.jwt || this.jwt.exp * 1000 < Date.now()) &&
            (this.token || serverToken)
        ) {
            if (!this.refreshPromise)
                this.refreshPromise = new Promise(async (resolve, reject) => {
                    try {
                        let token = this.token;

                        if (!token || (!!serverToken && token !== serverToken))
                            token = serverToken;

                        if (!!token) {
                            try {
                                const response = await fetch(
                                    `${
                                        Configuration.servicesPath
                                    }/api/authorize/refresh/${encodeURIComponent(
                                        token,
                                    )}`,
                                );

                                switch (response.status) {
                                    case 401:
                                        this.token = null;
                                        this.rawJWT = null;
                                        this.jwt = null;
                                        break;
                                    case 500:
                                        let error =
                                            (await response.json()) as ServerError;
                                        error.message = error.exceptionMessage;
                                        throw error;
                                    case 200:
                                        const jwtData = await response.text();
                                        const jqt =
                                            Utilities.parseAuthJWT(jwtData);

                                        if (!!jqt) {
                                            this.token = token;
                                            this.rawJWT = jwtData;
                                            this.jwt = jqt.payload;
                                        }

                                        this.storeState();
                                        break;
                                }
                            } catch (error) {
                                Logger.error("Error refreshing session JWT");
                                reject(error);
                                return;
                            }
                        }

                        resolve();
                    } finally {
                        this.refreshPromise = null;
                    }
                });

            await this.refreshPromise;
        }

        if (!this.jwt || this.jwt.exp * 1000 < Date.now()) {
            if (!!serverToken) return null;

            document.location = this.getAuthPath();

            await new Promise(() => {}); // Wait indefinitely for the page to reload

            return null;
        }

        return this.rawJWT;
    }
    getAuthPath(response?: string) {
        const authPath = `${Configuration.authenticationPath}/Authenticate/${Configuration.applicationToken}`;

        return `${authPath}?jwtrequest=${Utilities.getAuthJWT(
            authPath,
            response,
        )}`;
    }
    getLogoutPath(response?: string) {
        const authPath = `${Configuration.authenticationPath}/Logout/${Configuration.applicationToken}`;

        return `${authPath}?jwtrequest=${Utilities.getAuthJWT(
            authPath,
            response,
        )}`;
    }
    getSessionEncryptionKey() {
        if (!this.jwt) return null;

        if (this.jwt.exp * 1000 < Date.now()) return null;

        return this.jwt.encryptionkey;
    }
}

export default ApplicationState;
